import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useAnalytics } from "../hooks/useAnalytics";
import { buildContactEmail, buildMailtoUrl } from "../utils/contactEmail";
import "./HebrewCV.css";
import "./HebrewCVProjects.css";

const PAGE_TITLE = "ניב רומנו | קורות חיים אינטראקטיביים";
const PAGE_DESCRIPTION =
  "הסיפור, הניסיון והפרויקטים של ניב רומנו — דרך תוצאות שאפשר לראות ולבדוק.";

type ProjectLink = {
  label: string;
  href: string;
  analyticsType: "github" | "demo";
};

type CVProject = {
  id: string;
  number: string;
  title: string;
  field?: string;
  icon: string;
  iconAlt: string;
  supportingImage?: string;
  supportingImageAlt?: string;
  supportingImagePosition?: string;
  highlight?: string;
  facts: string[];
  technologies: string[];
  links: ProjectLink[];
  status?: string;
};

const projects: CVProject[] = [
  {
    id: "creators-command-dashboard",
    number: "01",
    title: "מרכז שליטה ליוצרי תוכן",
    field: "מוצר · אוטומציה · נתונים",
    icon: "/images/projects/creators-command-dashboard.webp",
    iconAlt: "איור תלת־ממדי של מרכז שליטה עם לוח תוכן, נתונים ותהליכי אוטומציה",
    highlight: "השקה של 60 יום, משימות ותוצאות רשת במקום אחד",
    facts: [
      "בניתי והעליתי לאוויר מרכז שליטה להשקת יוצרים בת 60 יום, שמתרגם את יעדי הקמפיין לספרינטים שבועיים, משימות יומיות, כרטיסי תוכן ותסריטים ומעקב מדיד אחר הפרסום.",
      "חיברתי את אינסטגרם וטיקטוק באמצעות OAuth, עם סנכרון אוטומטי שמרכז נתוני חשבון, תוכן וביצועים בלוח בקרה אחד.",
      "מימשתי אימות מאובטח המוגבל לבעלים, עדכונים בזמן אמת ושמירה קבועה של נתוני הקמפיין.",
    ],
    technologies: ["Next.js", "TypeScript", "Neon PostgreSQL", "Drizzle ORM", "Better Auth", "Ably", "Vercel"],
    links: [],
    status: "פעיל",
  },
  {
    id: "job-outreach-automation",
    number: "02",
    title: "אוטומציית חיפוש עבודה",
    field: "אוטומציה · איסוף נתונים · תפעול",
    icon: "/images/projects/job-outreach-automation.webp",
    iconAlt: "איור תלת־ממדי של תיק עבודה, מטרה, הודעה ותהליך פניות אוטומטי",
    highlight: "איסוף משרות, הגשות, פניות ומעקב במערכת עצמאית",
    facts: [
      "בניתי מערכת עצמאית שאוספת משרות ממספר מקורות ואתרי קריירה, מעשירה אותן ומסווגת לפי תפקיד, שפה, מקור והתאמה למועמד.",
      "אוטומציית ההגשה בוחרת את קורות החיים המתאימים, מתעדת ניסיונות וסטטוסים, מאמתת שההגשה הושלמה ומונעת ניסיונות חוזרים בפרק זמן מוגדר.",
      "פיתחתי תהליך לפניות הפניה: מחקר חברות ואנשים, איתור דרכי קשר, יצירת טיוטות מותאמות, בקרת איכות ומעקב אחר המשפך.",
      "המערכת פועלת על לינוקס וכוללת לוח ניטור עם סינון.",
    ],
    technologies: ["Hermes", "Python", "FastAPI", "Uvicorn", "Playwright", "HTTPX", "BeautifulSoup", "Linux"],
    links: [],
    status: "פעיל",
  },
  {
    id: "isl-recognition",
    number: "03",
    title: "זיהוי שפת הסימנים בזמן אמת",
    field: "למידת מכונה · ראייה ממוחשבת",
    icon: "/images/projects/isl-recognition.webp",
    iconAlt: "איור תלת־ממדי של יד בתוך מסגרת זיהוי עם נקודות מעקב",
    supportingImage: "/images/isl-recognition.jpg",
    supportingImageAlt: "ממשק מערכת לזיהוי שפת הסימנים הישראלית בזמן אמת",
    highlight: "91% דיוק · 22 אותיות · 30+ פריימים לשנייה",
    facts: [
      "אספתי ועיבדתי יותר מ־11,000 דוגמאות עבור 22 אותיות.",
      "אימנתי מודל שפועל בזמן אמת ומגיע ל־91% דיוק.",
      "קיצרתי את זמן האימון ב־25% ובניתי את ממשק הבדיקה.",
    ],
    technologies: ["Python", "TensorFlow", "CNN", "MediaPipe", "OpenCV"],
    links: [
      { label: "לקוד בגיטהאב", href: "https://github.com/Niv-Ro/ISL-Recognition", analyticsType: "github" },
      { label: "לצפייה בהדגמה", href: "https://youtu.be/k442R5vOBKg", analyticsType: "demo" },
    ],
    status: "מערכת עובדת",
  },
  {
    id: "tripbuddy",
    number: "04",
    title: "טריפ באדי — רשת חברתית למטיילים",
    field: "פול סטאק · תקשורת בזמן אמת",
    icon: "/images/projects/tripbuddy.webp",
    iconAlt: "איור תלת־ממדי של כדור הארץ, מסלול טיול, מיקומים ושיחה",
    supportingImage: "/images/tripbuddy.jpg",
    supportingImageAlt: "מסכי פלטפורמת טריפ־באדי לתכנון טיולים וחיבור בין מטיילים",
    highlight: "12+ ממשקי תכנות · מסרים בזמן אמת · 195+ מדינות",
    facts: [
      "בנינו חזית ושרת נפרדים עם אימות משתמשים ומסרים בזמן אמת.",
      "פיתחנו חיפוש מטיילים, קבוצות, תוכן ותכנון מסלול.",
      "הוספנו גלובוס תלת־ממדי שמציג יותר מ־195 מדינות.",
    ],
    technologies: ["React", "Next.js", "Node.js", "MongoDB", "Firebase", "Socket.IO"],
    links: [
      { label: "לקוד החזית", href: "https://github.com/Niv-Ro/TripBuddy_Frontend", analyticsType: "github" },
      { label: "לקוד השרת", href: "https://github.com/Niv-Ro/TripBuddy_Backend", analyticsType: "github" },
      { label: "לצפייה בהדגמה", href: "https://youtu.be/bPHdT6LLyZk", analyticsType: "demo" },
    ],
    status: "פרויקט צוות",
  },
  {
    id: "work-shift",
    number: "05",
    title: "אפליקציית ניהול משמרות ושכר",
    field: "יישומון אנדרואיד · תפעול עובדים",
    icon: "/images/projects/work-shift.webp",
    iconAlt: "איור תלת־ממדי של לוח משמרות, שעון, החלפה ותלוש שכר",
    supportingImage: "/images/work-shift-app.jpg",
    supportingImageAlt: "מסכי יישומון אנדרואיד לניהול משמרות ושכר",
    supportingImagePosition: "top",
    highlight: "כ־8 שעות עבודה נחסכות בכל חודש",
    facts: [
      "פיתחתי מסלולים נפרדים לעובד ולמנהל.",
      "היישומון מנהל משמרות, שעות, החלפות ותלושי שכר בזמן אמת.",
      "חיברתי את סידור העבודה ליומן גוגל.",
    ],
    technologies: ["Java", "Android", "Firebase", "Google Calendar API"],
    links: [
      { label: "לקוד בגיטהאב", href: "https://github.com/Niv-Ro/Work-Shift-App", analyticsType: "github" },
      { label: "לצפייה בהדגמה", href: "https://youtube.com/shorts/2mFZ398Upvo", analyticsType: "demo" },
    ],
    status: "אב־טיפוס עובד",
  },
  {
    id: "computer-security",
    number: "06",
    title: "מערכת אבטחה ואימות אתרים",
    field: "פרויקט אקדמי · פיתוח מאובטח",
    icon: "/images/projects/computer-security.webp",
    iconAlt: "איור תלת־ממדי של מגן מפוצל בין אתר פגיע לאתר מוגן",
    supportingImage: "/images/compsec.jpg",
    supportingImageAlt: "מסכי פרויקט אקדמי המדגים אימות משתמשים ואבטחת יישומי ווב",
    supportingImagePosition: "top",
    highlight: "מימוש פגיע מול מימוש מוגן באותה מערכת",
    facts: [
      "עבדתי בצוות של שלושה על אימות משתמשים ואחסון סיסמאות.",
      "בנינו תרחישים של הזרקת SQL וסקריפטים חוצי אתרים.",
      "השווינו בין זרימה פגיעה לזרימה מוגנת עם HMAC-SHA256 ובדיקות קלט.",
    ],
    technologies: ["C#", "ASP.NET", "MySQL", "HMAC-SHA256"],
    links: [
      { label: "לקוד בגיטהאב", href: "https://github.com/Niv-Ro/Project_computer_security", analyticsType: "github" },
      { label: "לצפייה בהדגמה", href: "https://youtu.be/uVdy_sMTN-Y", analyticsType: "demo" },
    ],
    status: "פרויקט אקדמי",
  },
  {
    id: "tetris",
    number: "07",
    title: "טטריס עם חוקי משחק מורחבים",
    field: "מנוע משחק · לוגיקה בזמן אמת",
    icon: "/images/projects/tetris.webp",
    iconAlt: "איור תלת־ממדי של משחק בלוקים עם חלק ייחודי בצורת פלוס",
    supportingImage: "/images/tetris.jpg",
    supportingImageAlt: "מסך משחק טטריס שפותח בשפת סי שארפ",
    highlight: "8 צורות · חוקי התנגשות וסיבוב · שליטה מלאה",
    facts: [
      "פיתחתי את לולאת המשחק, זיהוי ההתנגשויות וניקוי השורות.",
      "הוספתי צורת פלוס שמינית ומיקום פתיחה משתנה.",
      "בניית הקלט, הסיבוב והשליטה מהמקלדת נעשתה מאפס.",
    ],
    technologies: ["C#", ".NET"],
    links: [
      { label: "לקוד בגיטהאב", href: "https://github.com/Niv-Ro/Tetris_Game", analyticsType: "github" },
      { label: "לצפייה בהדגמה", href: "https://youtu.be/WH8YJ1qhi6A", analyticsType: "demo" },
    ],
    status: "משחק עובד",
  },
];

type ProjectShelf = {
  id: string;
  title: string;
  items: CVProject[];
};

const projectShelves: ProjectShelf[] = [
  {
    id: "code",
    title: "קוד",
    items: projects,
  },
  {
    id: "tools",
    title: "כלי עזר",
    items: [
      {
        id: "wake-up-system",
        number: "01",
        title: "אוטומציית שעון מעורר מוזיקלי",
        field: "אוטומציה ביתית · אינטגרציות",
        icon: "/images/projects/wake-up-system.webp",
        iconAlt: "איור תלת־ממדי של זריחה, טלוויזיה, מוזיקה ושעון מעורר",
        highlight: "הטלוויזיה, המוזיקה ועוצמת הקול מתואמות לזרימת בוקר אחת",
        facts: [
          "שגרת SmartThings באייפון מדליקה את הטלוויזיה, ושירות שרץ על המק מזהה אותה ומפעיל את Spotify דרך Tizen ו־Spotify Connect.",
          "לפני תחילת הנגינה המערכת מגדירה עוצמה ב־Denon AVR-X3200W וקוראת את תשובת הרסיבר כדי לוודא את הערך המדויק.",
          "ההפעלה מתבצעת פעם אחת בכל מחזור הדלקה, עם מתג שליטה מהטלפון, עבודה ברשת המקומית ושמירת סודות ב־macOS Keychain.",
        ],
        technologies: ["Python", "SmartThings", "Samsung Tizen", "Spotify Web API", "macOS LaunchAgent", "Denon LAN"],
        links: [],
        status: "מערכת ביתית פעילה",
      },
      {
        id: "progress-system",
        number: "02",
        title: "מערכת למעקב התקדמות במשימות",
        field: "כלי פרודוקטיביות · macOS",
        icon: "/images/projects/progress-system.webp",
        iconAlt: "איור תלת־ממדי של לוח קנבן עם משימות שעוברות בין שלושה שלבים",
        highlight: "לוח אישי שמרכז המתנה, עבודה והשלמה במקום אחד",
        facts: [
          "בניתי יישומון macOS ב־SwiftUI שמחלק משימות לשלושה טורים: רשימת המתנה, עבודה והושלם.",
          "אפשר להוסיף משימה במהירות, לערוך אותה, לסמן בדגל ולהעביר אותה בין שלבים בגרירה או דרך תפריט.",
          "המשימות נשמרות מקומית כ־JSON ב־Application Support באמצעות כתיבה אטומית.",
        ],
        technologies: ["Swift", "SwiftUI", "Combine", "Codable", "macOS"],
        links: [],
        status: "יישומון macOS",
      },
      {
        id: "capchouk",
        number: "03",
        title: "פלאגין כתוביות לדה וינצ׳י",
        field: "כלי וידאו · תמלול מקומי · DaVinci Resolve",
        icon: "/images/projects/capchouk.webp",
        iconAlt: "איור תלת־ממדי של סביבת יצירת כתוביות עם גל קול, תזמון ותנועה",
        highlight: "מתמלול עברי ואנגלי לכתוביות מעוצבות, מתוזמנות וניתנות לעריכה",
        facts: [
          "אני מפתח סביבת עבודה מקומית ליצירת כתוביות בעברית, באנגלית ובטקסט מעורב, עם תמלול, תיקון ותזמון ברמת המילה.",
          "המערכת מחברת תצוגה מקדימה קבועה לשישה שלבים ממוקדים: מקור, תמלול, סנכרון, עיצוב, תנועה ומסירה.",
          "הפרויקט כולל חיבור ל־DaVinci Resolve ול־Fusion, חבילות עיצוב ותהליך יצירה מבוקר שמעדכן רק פריטים שבבעלות CAPCHOUK; הוא עדיין בפיתוח.",
        ],
        technologies: ["Python", "Rust", "Swift", "Lua", "DaVinci Resolve", "Fusion", "Whisper"],
        links: [],
        status: "בפיתוח פעיל",
      },
    ],
  },
  {
    id: "sport",
    title: "ספורט",
    items: [
      {
        id: "seven-years-training", number: "01", title: "7 שנים של אימונים",
        icon: "/images/projects/seven-years-training.webp",
        iconAlt: "איור תלת־ממדי של ספורטאי במסלול אימוני מכשולים מתמשך",
        facts: [], technologies: [], links: [],
      },
      {
        id: "israel-championship-2024", number: "02", title: "אלוף ישראל בנינג׳ה לשנת 2024",
        icon: "/images/projects/israel-championship-2024.webp",
        iconAlt: "איור תלת־ממדי של זכייה במקום הראשון באליפות ישראל בנינג׳ה",
        facts: [], technologies: [], links: [],
      },
      {
        id: "world-championships", number: "03", title: "שתי אליפויות עולם",
        icon: "/images/projects/world-championships.webp",
        iconAlt: "איור תלת־ממדי של השתתפות בשני מסלולי מכשולים בין־לאומיים",
        facts: [], technologies: [], links: [],
      },
      {
        id: "weight-transformation", number: "04", title: "ירידה מ־90 ל־70 ק״ג",
        icon: "/images/projects/weight-transformation.webp",
        iconAlt: "איור תלת־ממדי של גבר לפני ואחרי שינוי כושר ומשקל בתהליך בריא",
        facts: [], technologies: [], links: [],
      },
    ],
  },
  {
    id: "more",
    title: "נוספים",
    items: [
      {
        id: "wireless-speaker", number: "01", title: "רמקול אלחוטי שבניתי לבד מאפס",
        icon: "/images/projects/wireless-speaker.webp",
        iconAlt: "איור תלת־ממדי של רמקול אלחוטי בעבודת יד עם מעגל וכלי הלחמה",
        facts: [], technologies: [], links: [],
      },
      {
        id: "degree-completion", number: "02", title: "סיום תואר של 3 שנים ב-6 חודשי למידה מרוכזת",
        icon: "/images/projects/degree-completion.webp",
        iconAlt: "איור תלת־ממדי של מסלול אקדמי ארוך שמסתיים בתקופת למידה מרוכזת ובסיום תואר",
        facts: [], technologies: [], links: [],
      },
    ],
  },
];

const allProjects = projectShelves.flatMap((shelf) => shelf.items);

function buildYouTubeEmbedUrl(href: string) {
  try {
    const url = new URL(href);
    const hostname = url.hostname.replace(/^www\./, "");
    let videoId = "";

    if (hostname === "youtu.be") {
      videoId = url.pathname.split("/").filter(Boolean)[0] || "";
    } else if (hostname === "youtube.com") {
      videoId = url.searchParams.get("v") || "";
      if (!videoId) {
        const [route, id] = url.pathname.split("/").filter(Boolean);
        if (route === "shorts" || route === "embed") videoId = id || "";
      }
    }

    if (!/^[A-Za-z0-9_-]{11}$/.test(videoId)) return null;
    return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
  } catch {
    return null;
  }
}

const workExperience = [
  {
    period: "2020–2021",
    role: "טכנאי תקשורת אופטית",
    organization: "Ciena",
    facts: [
      "תחזקתי מערכות אופטיות עבור יותר מ־15 לקוחות ארגוניים, כולל גופים במערכת הביטחון.",
      "צמצמתי פיזור אות ב־30% ונתתי מענה באותו יום לרוב התקלות הטכניות.",
      "כתבתי יותר מ־10 נהלי עבודה ששיפרו את יעילות הצוות.",
    ],
  },
  {
    period: "2017–2020",
    role: "טכנאי מערכות תקשורת",
    organization: "צה״ל",
    facts: [
      "תמכתי בתפעול אמין של מערכות VOIP/DWDM קריטיות.",
      "הגדרתי יותר מ־50 רכיבי רשת של Cisco, Juniper ו־Motorola.",
      "הכשרתי יותר מ־20 טכנאים צעירים.",
    ],
  },
];

type ProjectModalProps = {
  project: CVProject;
  onClose: () => void;
  onExternalLink: (linkType: "github" | "demo", projectId: string) => void;
};

function ProjectModal({ project, onClose, onExternalLink }: ProjectModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalCardRef = useRef<HTMLElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const demoLink = project.links.find((link) => link.analyticsType === "demo");
  const githubLinks = project.links.filter((link) => link.analyticsType === "github");
  const videoEmbedUrl = demoLink ? buildYouTubeEmbedUrl(demoLink.href) : null;
  const hasVideoPreview = Boolean(project.supportingImage && videoEmbedUrl);

  useEffect(() => {
    const previousRootOverflow = document.documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key === "Tab") {
        const focusable = Array.from(
          modalCardRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
          ) || []
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.documentElement.style.overflow = previousRootOverflow;
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const titleId = `cv-project-modal-title-${project.id}`;

  return (
    <div
      className="cv-project-modal"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.section
        className="cv-project-modal__card"
        ref={modalCardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <button
          className="cv-project-modal__close"
          type="button"
          onClick={onClose}
          aria-label={`סגירת ${project.title}`}
          ref={closeButtonRef}
        >
          ×
        </button>

        <div className="cv-project-modal__scroller">
          <div className={`cv-project-modal__artwork${hasVideoPreview && !isVideoPlaying ? " cv-project-modal__artwork--preview" : ""}`}>
            {isVideoPlaying && videoEmbedUrl ? (
              <iframe
                src={videoEmbedUrl}
                title={`סרטון הדגמה של ${project.title}`}
                allow="autoplay; encrypted-media; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <img
                src={project.supportingImage || project.icon}
                alt={project.supportingImageAlt || project.iconAlt}
                width="1200"
                height="900"
                style={{ objectPosition: project.supportingImage ? project.supportingImagePosition || "center" : "center" }}
              />
            )}
            {hasVideoPreview && !isVideoPlaying && (
              <button
                className="cv-project-modal__play"
                type="button"
                aria-label={`הפעלת סרטון הדגמה של ${project.title}`}
                onClick={() => {
                  setIsVideoPlaying(true);
                  onExternalLink("demo", project.id);
                }}
              >
                <span aria-hidden="true" />
              </button>
            )}
          </div>

          <div className="cv-project-modal__content">
            {(project.field || project.status) && (
              <div className="cv-project-modal__meta">
                {project.field && <p>{project.field}</p>}
                {project.status && <span>{project.status}</span>}
              </div>
            )}
            <h2 id={titleId}>{project.title}</h2>
            {githubLinks.length > 0 && (
              <div className="cv-project-modal__github-links">
                {githubLinks.map((link) => (
                  <a
                    key={`${project.id}-${link.label}`}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => onExternalLink("github", project.id)}
                  >
                    <span aria-hidden="true">↗</span>
                    {link.label}
                  </a>
                ))}
              </div>
            )}
            {project.highlight && <p className="cv-project-modal__highlight">{project.highlight}</p>}

            {project.facts.length > 0 && (
              <ul className="cv-project-modal__facts">
                {project.facts.map((fact) => <li key={fact}>{fact}</li>)}
              </ul>
            )}

            {project.technologies.length > 0 && (
              <div className="cv-project-modal__technologies">
                <h3>טכנולוגיות</h3>
                <div className="cv-project-modal__tags" dir="ltr">
                  {project.technologies.map((technology) => <span key={technology}>{technology}</span>)}
                </div>
              </div>
            )}

          </div>
        </div>
      </motion.section>
    </div>
  );
}

export default function HebrewCV() {
  const {
    trackPageView,
    trackProjectView,
    trackExternalLink,
    trackContactInteraction,
  } = useAnalytics();
  const [contactStatus, setContactStatus] = useState("");
  const [emailCopyStatus, setEmailCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const emailCopyResetTimer = useRef<number | null>(null);
  const lastProjectTrigger = useRef<HTMLButtonElement | null>(null);
  const activeProject = allProjects.find((project) => project.id === activeProjectId);

  const openProject = (project: CVProject, trigger: HTMLButtonElement) => {
    lastProjectTrigger.current = trigger;
    setActiveProjectId(project.id);
    trackProjectView(project.id, "case_study");
  };

  const closeProject = () => {
    setActiveProjectId(null);
    window.requestAnimationFrame(() => lastProjectTrigger.current?.focus());
  };

  const showEmailCopyStatus = (status: "copied" | "failed") => {
    if (emailCopyResetTimer.current !== null) {
      window.clearTimeout(emailCopyResetTimer.current);
    }

    setEmailCopyStatus(status);
    emailCopyResetTimer.current = window.setTimeout(() => {
      setEmailCopyStatus("idle");
      emailCopyResetTimer.current = null;
    }, 5000);
  };

  useEffect(() => () => {
    if (emailCopyResetTimer.current !== null) {
      window.clearTimeout(emailCopyResetTimer.current);
    }
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    const previousDirection = document.documentElement.dir;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;

    document.title = PAGE_TITLE;
    document.documentElement.lang = "he";
    document.documentElement.dir = "rtl";
    if (description) description.content = PAGE_DESCRIPTION;
    const socialSelectors = [
      'meta[property="og:title"]',
      'meta[property="og:description"]',
      'meta[name="twitter:title"]',
      'meta[name="twitter:description"]',
    ];
    const socialPreviousValues = socialSelectors.map((selector) => {
      const element = document.querySelector<HTMLMetaElement>(selector);
      const previous = element?.content;
      if (element) {
        element.content = selector.includes("description") ? PAGE_DESCRIPTION : PAGE_TITLE;
      }
      return { element, previous };
    });
    const socialImageUrl = new URL("/og-cv.png", window.location.origin).href;
    const createdMetaElements: HTMLMetaElement[] = [];
    [
      { attribute: "property", key: "og:image", content: socialImageUrl },
      { attribute: "property", key: "og:locale", content: "he_IL" },
      { attribute: "name", key: "twitter:image", content: socialImageUrl },
    ].forEach(({ attribute, key, content }) => {
      const selector = `meta[${attribute}="${key}"]`;
      let element = document.querySelector<HTMLMetaElement>(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
        createdMetaElements.push(element);
      }
      element.content = content;
    });

    trackPageView("/cv");

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
      document.documentElement.dir = previousDirection;
      if (description && previousDescription) description.content = previousDescription;
      socialPreviousValues.forEach(({ element, previous }) => {
        if (element && previous) element.content = previous;
      });
      createdMetaElements.forEach((element) => element.remove());
    };
  }, [trackPageView]);

  const copyText = async (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    if (!copied) throw new Error("copy-failed");
  };

  const sharePage = async () => {
    const shareData = {
      title: PAGE_TITLE,
      text: "מכירים מישהו שמגייס? הכירו את ניב רומנו.",
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setContactStatus("העמוד שותף");
        trackContactInteraction("cv_share_success");
      } else {
        await copyText(window.location.href);
        setContactStatus("הקישור הועתק");
        trackContactInteraction("cv_link_copied");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setContactStatus("לא הצלחנו לשתף. אפשר להעתיק את הכתובת משורת הדפדפן.");
      trackContactInteraction("cv_share_failed");
    }
  };

  const copyEmail = async () => {
    try {
      await copyText(buildContactEmail());
      showEmailCopyStatus("copied");
      setContactStatus("כתובת המייל הועתקה");
      trackContactInteraction("cv_email_copied");
    } catch {
      showEmailCopyStatus("failed");
      setContactStatus("לא הצלחנו להעתיק את כתובת המייל");
      trackContactInteraction("cv_email_copy_failed");
    }
  };

  const scrollProjectRow = (rowId: string, direction: -1 | 1) => {
    const row = document.getElementById(rowId);
    if (!row) return;

    row.scrollBy({
      left: direction * row.clientWidth * 0.8,
      behavior: "smooth",
    });
  };

  return (
    <div className="cv-page" dir="rtl" lang="he">
      <a className="cv-skip-link" href="#cv-main">
        דילוג לתוכן
      </a>

      <div className="cv-background" aria-hidden="true">
        <span className="cv-background__orb cv-background__orb--one" />
        <span className="cv-background__orb cv-background__orb--two" />
        <span className="cv-background__grid" />
      </div>

      <header className="cv-header">
        <a className="cv-brand" href="/" aria-label="חזרה לתיק העבודות של ניב רומנו">
          <span className="cv-brand__mark" aria-hidden="true">נר</span>
          <span>ניב רומנו</span>
        </a>
        <nav className="cv-nav" aria-label="ניווט בעמוד">
          <a href="#projects">פרויקטים</a>
          <a href="#experience">ניסיון</a>
          <a href="#contact">קשר</a>
        </nav>
        <div className="cv-header__actions">
          <a className="cv-header__contact" href="#contact">
            יצירת קשר
          </a>
          <button className="cv-header__share" type="button" onClick={sharePage}>
            שיתוף העמוד
          </button>
        </div>
      </header>

      <main id="cv-main">
        <section className="cv-hero" aria-labelledby="cv-title">
          <span className="cv-hero__image-frame" aria-hidden="true">
            <img
              className="cv-hero__image"
              src="/images/niv-romano-portrait.jpg"
              alt=""
              width="886"
              height="886"
              fetchPriority="high"
            />
          </span>
          <span className="cv-hero__veil" aria-hidden="true" />

          <motion.div
            className="cv-hero__copy"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <p className="cv-hero__status">אני מחפש עבודה</p>
            <div className="cv-hero__message">
              <h1 id="cv-title">יכול לעשות הכל</h1>
              <strong>מכירים מישהו שמגייס? שלחו לו את העמוד הזה.</strong>
            </div>
          </motion.div>
        </section>

        <section className="cv-section cv-projects-section" id="projects" aria-labelledby="projects-title">
          <div className="cv-section__heading cv-section__heading--projects">
            <h2 id="projects-title">פרויקטים</h2>
          </div>

          <div className="cv-projects-shelves">
            {projectShelves.map((shelf) => {
              const shelfTitleId = `cv-shelf-${shelf.id}-title`;
              const shelfRowId = `cv-shelf-${shelf.id}`;

              return (
                <section className="cv-project-shelf" key={shelf.id}>
                  <div className="cv-project-shelf__header">
                    <h3 id={shelfTitleId}>{shelf.title}</h3>
                    <div className="cv-project-shelf__controls" aria-label={`גלילה בשורת ${shelf.title}`}>
                      <button type="button" onClick={() => scrollProjectRow(shelfRowId, 1)} aria-label={`גלילה ימינה בשורת ${shelf.title}`}>→</button>
                      <button type="button" onClick={() => scrollProjectRow(shelfRowId, -1)} aria-label={`גלילה שמאלה בשורת ${shelf.title}`}>←</button>
                    </div>
                  </div>
                  <div
                    className={`cv-project-row cv-project-row--${shelf.items.length > 2 ? "many" : "few"}`}
                    id={shelfRowId}
                    role="region"
                    aria-labelledby={shelfTitleId}
                    tabIndex={0}
                  >
                    {shelf.items.map((project) => {
                      const hasDetails = Boolean(
                        project.highlight ||
                        project.facts.length ||
                        project.technologies.length ||
                        project.links.length ||
                        project.supportingImage
                      );

                      return (
                        <article className="cv-project-tile" key={project.id}>
                        <div className="cv-project-tile__artwork">
                          <img
                            src={project.icon}
                            alt={project.iconAlt}
                            width="900"
                            height="900"
                            loading="lazy"
                          />
                        </div>
                        {hasDetails ? (
                          <button
                            className="cv-project-tile__footer cv-project-tile__trigger"
                            type="button"
                            onClick={(event) => openProject(project, event.currentTarget)}
                            aria-haspopup="dialog"
                          >
                            <span>{project.title}</span>
                            <span className="cv-project-tile__plus" aria-hidden="true">+</span>
                          </button>
                        ) : (
                          <div className="cv-project-tile__footer cv-project-tile__label">
                            <span>{project.title}</span>
                          </div>
                        )}
                      </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </section>

        <section className="cv-section cv-experience-section" id="experience" aria-labelledby="experience-title">
          <div className="cv-section__heading cv-section__heading--experience">
            <h2 id="experience-title">ניסיון</h2>
          </div>

          <div className="cv-experience-layout">
            <div className="cv-experience-list">
              {workExperience.map((experience) => (
                <article className="cv-experience-card" key={`${experience.organization}-${experience.period}`}>
                  <div className="cv-experience-card__heading">
                    <div>
                      <h3>{experience.role}</h3>
                      <p>{experience.organization}</p>
                    </div>
                    <bdi>{experience.period}</bdi>
                  </div>
                  <ul>
                    {experience.facts.map((fact) => <li key={fact}>{fact}</li>)}
                  </ul>
                </article>
              ))}
            </div>

            <div className="cv-experience-divider" aria-hidden="true" />

            <aside className="cv-education" aria-labelledby="education-title">
              <p className="cv-section__index">השכלה</p>
              <div className="cv-education__degree">
                <h3 id="education-title">תואר ראשון במדעי המחשב</h3>
                <bdi>2022–2025</bdi>
              </div>
              <p className="cv-education__organization">המכון הטכנולוגי חולון</p>
              <hr />
              <h3>שפות</h3>
              <div className="cv-education__languages">
                <p>עברית — שפת אם</p>
                <span aria-hidden="true">|</span>
                <p>אנגלית — רמה שוטפת</p>
              </div>
            </aside>
          </div>
        </section>

        <section className="cv-contact" id="contact" aria-labelledby="contact-title">
          <div>
            <p className="cv-section__index">יצירת קשר</p>
            <h2 id="contact-title">מכירים צוות שמגייס?</h2>
            <p>שלחו לו את העמוד.</p>
          </div>
          <div className="cv-contact__actions">
            <a href={buildMailtoUrl()} onClick={() => trackContactInteraction("cv_email_compose_open")}>שליחת מייל</a>
            <button
              className={`cv-contact__copy${emailCopyStatus === "idle" ? "" : ` cv-contact__copy--${emailCopyStatus}`}`}
              type="button"
              onClick={copyEmail}
              aria-label={emailCopyStatus === "copied"
                ? "✓ המייל הועתק"
                : emailCopyStatus === "failed"
                  ? "ההעתקה נכשלה"
                  : "העתקת כתובת מייל"}
            >
              <span className="cv-contact__copy-sizer" aria-hidden="true">העתקת כתובת מייל</span>
              <span className="cv-contact__copy-message">
                {emailCopyStatus === "copied"
                  ? "✓ המייל הועתק"
                  : emailCopyStatus === "failed"
                    ? "ההעתקה נכשלה"
                    : "העתקת כתובת מייל"}
              </span>
            </button>
            <a href="https://www.linkedin.com/in/niv-romano/" target="_blank" rel="noreferrer" onClick={() => trackExternalLink("linkedin")}>לינקדאין</a>
            <a href="https://github.com/Niv-Ro" target="_blank" rel="noreferrer" onClick={() => trackExternalLink("github")}>גיטהאב</a>
            <button type="button" onClick={sharePage}>שיתוף העמוד</button>
          </div>
          <p className="cv-share-status" aria-live="polite">{contactStatus}</p>
        </section>
      </main>

      <footer className="cv-footer">
        <span>© {new Date().getFullYear()} ניב רומנו</span>
        <a href="/">לתיק העבודות הראשי</a>
      </footer>

      {activeProject && (
        <ProjectModal
          project={activeProject}
          onClose={closeProject}
          onExternalLink={trackExternalLink}
        />
      )}
    </div>
  );
}
